<script setup>
    import { computed } from "vue"
    import checkPermissionConfig from "@/utils/checkPermissionConfig/index"
    import { useUserStore } from "@/stores/user"

    const props = defineProps({
        /**
         * 當前要符合的權限
         */
        requirePermission: {
            type: [String, Array],
            required: true,
        },
        /**
         * 資產物件，包含 department 創建者部門、status 物件狀態、creatorUuid 創建者 uuid、roleLevel 物件 level（角色）
         */
        assetData: {
            type: Object,
            required: false,
            default: null,
        },
        /**
         * 包含 requiredAll 需要全部符合或單一權限符合、requireValidateAssets 需要驗證相關的 assets，不傳入的話兩者預設都是 true
         */
        config: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    })

    const userStore = useUserStore()
    const { hasPermission } = useHasPermission(userStore, props)

    /**
     * @typedef {object} AssetData 資產物件
     * @property {string} department 創建者部門
     * @property {string} status 物件狀態
     * @property {string} creatorUuid 創建者 uuid
     * @property {number} roleLevel 物件 level（角色）
     */

    /**
     * @typedef {object} HasPermission 資產物件
     * @property {import("vue").Ref<boolean>} hasPermission 創建者部門
     */

    /**
     * 確認使用者是否擁有特定權限的計算屬性。
     * @param {object} userStore - userStore
     * @param {object} props - 組件的屬性。
     * @param {AssetData} props.assetData - 物件狀態
     * @returns {HasPermission} - 布林值的計算屬性，指示是否擁有所需權限。
     */
    function useHasPermission(userStore, props) {
        const hasPermission = computed(() => {
            return checkPermissionConfig(
                props.requirePermission,
                userStore,
                props.assetData,
                props.config
            )
        })
        return { hasPermission }
    }
</script>

<template>
    <div :style="{ display: hasPermission ? 'inline-block' : 'none' }">
        <slot :user-info="userStore.userInfo"></slot>
    </div>
</template>
